import { useHeatReportContext } from "@/pages/heat-report/context/HeatReportContext";
import {
  BorderedTablePaper,
  OutlinedTextarea,
} from "@/pages/heat-report/styles/HeatReportStyledComponents";
import { getMomentTz } from "@dexteel/mesf-core";
import { Box, Card, Grid, TextField, Typography } from "@mui/material";
import { AssetCard } from "../../../controls/AssetCard";
import DurationInput from "@/shared/data-entries/common/DurationInput";
import { CheckboxFieldDE } from "@/shared/data-entries/common/CheckboxFieldDE";
import { LADLE_CAR_COLORS } from "@/pages/lmf/data-entry/constants/ladleCarColors";

const moment = getMomentTz();
export const HeaderData = () => {
  const {
    state: { lmfProcessData, heat },
  } = useHeatReportContext();

  return (
    <BorderedTablePaper elevation={0}>
      <Box padding={1}>
        <Grid
          item
          xs={12}
          container
          spacing={2}
          key={lmfProcessData?.ProcessId}
        >
          {/* Column 1 - Asset Card + Ladle Car */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            container
            key={`${lmfProcessData?.ProcessId}-column-1`}
          >
            <Grid item xs={12} key={`${heat.HeatId}-asset`}>
              <AssetCard
                assetId={lmfProcessData?.AssetId ?? 0}
                assetName={lmfProcessData?.AssetName ?? ""}
              />
            </Grid>
            {lmfProcessData?.LadleCar != null && (
              <Grid item xs={12} key="ladle-car" sx={{ mt: 1 }}>
                <Card
                  variant="outlined"
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    userSelect: "none",
                    height: "35px",
                    backgroundColor:
                      lmfProcessData.LadleCar === 1
                        ? LADLE_CAR_COLORS.lc1.bg
                        : LADLE_CAR_COLORS.lc2.bg,
                    borderColor:
                      lmfProcessData.LadleCar === 1
                        ? LADLE_CAR_COLORS.lc1.border
                        : LADLE_CAR_COLORS.lc2.border,
                  }}
                >
                  <Typography sx={{ fontWeight: 500 }}>
                    {`Ladle Car ${lmfProcessData.LadleCar}`}
                  </Typography>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Column 2 */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            container
            key={`${lmfProcessData?.ProcessId}-column-2`}
          >
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={6} key="arrive-time">
                  <TextField
                    label="Arrive"
                    value={
                      lmfProcessData?.Start
                        ? moment(lmfProcessData.Start).format(
                            "HH:mm - MM/DD/YYYY",
                          )
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} key="ship-time">
                  <TextField
                    label="Ship"
                    value={
                      lmfProcessData?.End
                        ? moment(lmfProcessData.End).format(
                            "HH:mm - MM/DD/YYYY",
                          )
                        : ""
                    }
                    variant="outlined"
                    fullWidth
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} key="treatment-duration">
                  <TextField
                    fullWidth
                    label={"Treatment Duration [min]"}
                    variant={"outlined"}
                    value={
                      lmfProcessData?.ProcessTime
                        ? lmfProcessData?.ProcessTime
                        : 0
                    }
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} key="lance-time">
                  <DurationInput
                    label={"Emergency Lance Time"}
                    initMinutes={
                      typeof lmfProcessData?.EmergencyLanceTime === "number"
                        ? lmfProcessData.EmergencyLanceTime
                        : undefined
                    }
                    readOnly
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Column 3 */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            container
            key={`${lmfProcessData?.ProcessId}-column-3`}
          >
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={6} key="lmf-power-on">
                  <DurationInput
                    label={"Power On [hh:mm]"}
                    initMinutes={
                      typeof lmfProcessData?.PowerOn === "number"
                        ? lmfProcessData.PowerOn
                        : undefined
                    }
                    readOnly
                  />
                </Grid>

                <Grid item xs={6} key="lmf-energy">
                  <TextField
                    label="Energy [kWh]"
                    value={lmfProcessData?.EnergyConsumption ?? ""}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={6} key="lmf-foreman">
                  <TextField
                    label="Foreman"
                    value={lmfProcessData?.Foreman ?? ""}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} key="lmf-operator">
                  <TextField
                    label="Operator"
                    value={lmfProcessData?.OperatorName ?? ""}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Column 4 */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            container
            key={`${lmfProcessData?.ProcessId}-column-4`}
          >
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <DurationInput
                    label="Plug Inside Time"
                    mode="mm:ss"
                    initMinutes={lmfProcessData?.PlugInsideTime ?? 0}
                    readOnly
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Plug Inside Vol [SCF]"
                    variant="outlined"
                    fullWidth
                    value={lmfProcessData?.PlugInsideVol?.toFixed(2) || ""}
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DurationInput
                    label="Plug Outside Time"
                    mode="mm:ss"
                    initMinutes={lmfProcessData?.PlugOutsideTime ?? 0}
                    readOnly
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Plug Outside Vol [SCF]"
                    variant="outlined"
                    fullWidth
                    value={lmfProcessData?.PlugOutsideVol?.toFixed(2) || ""}
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Column 5 */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            container
            key={`${lmfProcessData?.ProcessId}-column-5`}
          >
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12} container direction="row">
                  <Grid item xs={4}>
                    <CheckboxFieldDE
                      label="Elec. 1"
                      value={!!lmfProcessData?.Electrode1}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <CheckboxFieldDE
                      label="Elec. 2"
                      value={!!lmfProcessData?.Electrode2}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <CheckboxFieldDE
                      label="Elec. 3"
                      value={!!lmfProcessData?.Electrode3}
                      disabled
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <CheckboxFieldDE
                    label="Manual Arrive"
                    value={!!lmfProcessData?.IsArriveManual}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <CheckboxFieldDE
                    label="Manual Ship"
                    value={!!lmfProcessData?.IsShipManual}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <CheckboxFieldDE
                    label="EAF Slagged in Ladle"
                    value={!!lmfProcessData?.EAFSlaggedInLadle}
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Column 6 */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2}
            container
            key={`${lmfProcessData?.ProcessId}-column-6`}
          >
            <Grid item xs={12} md={12}>
              <OutlinedTextarea
                placeholder="Notes"
                rows={5}
                value={lmfProcessData?.Comments ?? ""}
                disabled
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </BorderedTablePaper>
  );
};
